import React, { useEffect, useState, useCallback } from 'react';
import { financeiroAPI, clientsAPI, empresaAPI } from '../../lib/api';
import { useModulosAtivos } from '../../hooks/useModulosAtivos';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  BarChart3, Printer, X, TrendingUp, TrendingDown, DollarSign, FileText, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { gerarRelatorioPDF } from './pdf/RelatorioFinanceiroPDF';
import ReportFavorites from '../../components/ReportFavorites';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d + (d.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('pt-BR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pt-BR') : '—';

const STATUS_LABEL = {
  em_andamento: 'Em Andamento', ativo: 'Em Andamento',
  pausado: 'Pausado', aguardando_cliente: 'Aguardando Cliente',
  aguardando_pagamento: 'Aguardando Pagamento',
  concluido: 'Concluído', cancelado: 'Cancelado',
};

const emptyFilters = {
  status: '',
  responsavel_id: '',
  cliente_id: '',
  tipo_servico_id: '',
  tipo_despesa_id: '',
  mes: '',
  data_inicio: '',
  data_fim: '',
  com_manejo: '',
  incluir_finalizados: true,
  somente_pagos: false,
};

export default function FinanceiroRelatorio() {
  const { financeiroAtivo, loading: loadingMod } = useModulosAtivos();
  const [filters, setFilters] = useState(emptyFilters);
  const [detalhado, setDetalhado] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [empresaConfig, setEmpresaConfig] = useState(null);

  const [responsaveis, setResponsaveis] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tiposDespesa, setTiposDespesa] = useState([]);

  useEffect(() => {
    if (!financeiroAtivo) return;
    Promise.all([
      financeiroAPI.listResponsaveis().then(r => setResponsaveis(r.data || [])).catch(() => {}),
      clientsAPI.list().then(r => setClientes(r.data || [])).catch(() => {}),
      financeiroAPI.listServiceTypes().then(r => setTipos(r.data || [])).catch(() => {}),
      financeiroAPI.listCategories('despesa').then(r => setTiposDespesa(r.data || [])).catch(() => {}),
      // Buscar config da empresa (nome + logo)
      empresaAPI.getConfig().then(r => setEmpresaConfig(r.data)).catch(() => setEmpresaConfig(null)),
    ]);
  }, [financeiroAtivo]);

  const gerar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.responsavel_id) params.responsavel_id = filters.responsavel_id;
      if (filters.cliente_id) params.cliente_id = filters.cliente_id;
      if (filters.tipo_servico_id) params.tipo_servico_id = filters.tipo_servico_id;
      if (filters.tipo_despesa_id) params.tipo_despesa_id = filters.tipo_despesa_id;
      if (filters.mes) params.mes = filters.mes;
      if (filters.data_inicio) params.data_inicio = filters.data_inicio;
      if (filters.data_fim) params.data_fim = filters.data_fim;
      if (filters.com_manejo === 'sim') params.com_manejo = true;
      params.incluir_finalizados = filters.incluir_finalizados;
      if (filters.somente_pagos) params.somente_pagos = true;

      const r = await financeiroAPI.relatorio(params);
      setData(r.data);
    } catch (e) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (financeiroAtivo && !data) gerar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeiroAtivo]);

  const handleExportPDF = async () => {
    if (!data) return;
    setExporting(true);
    try {
      // Monta labels amigáveis dos filtros aplicados
      const filtrosLabels = [];
      if (filters.status) filtrosLabels.push({ label: 'Status', value: STATUS_LABEL[filters.status] || filters.status });
      if (filters.responsavel_id) {
        const r = responsaveis.find(x => x.id === filters.responsavel_id);
        filtrosLabels.push({ label: 'Responsável', value: r?.nome || filters.responsavel_id });
      }
      if (filters.cliente_id) {
        const c = clientes.find(x => x.id === filters.cliente_id);
        filtrosLabels.push({ label: 'Cliente', value: c?.nome_completo || filters.cliente_id });
      }
      if (filters.tipo_servico_id) {
        const t = tipos.find(x => x.id === filters.tipo_servico_id);
        filtrosLabels.push({ label: 'Tipo de Serviço', value: t?.nome || filters.tipo_servico_id });
      }
      if (filters.tipo_despesa_id) {
        const t = tiposDespesa.find(x => x.id === filters.tipo_despesa_id);
        filtrosLabels.push({ label: 'Tipo de Despesa', value: t?.nome || filters.tipo_despesa_id });
      }
      if (filters.somente_pagos) {
        filtrosLabels.push({ label: 'Filtro', value: 'Somente serviços pagos' });
      }
      if (filters.mes) {
        const [y, m] = filters.mes.split('-');
        const nome = new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        filtrosLabels.push({ label: 'Mês', value: nome });
      }
      if (filters.data_inicio || filters.data_fim) {
        const di = filters.data_inicio ? fmtDate(filters.data_inicio) : '—';
        const df = filters.data_fim ? fmtDate(filters.data_fim) : '—';
        filtrosLabels.push({ label: 'Período', value: `${di} até ${df}` });
      }
      if (filters.com_manejo === 'sim') filtrosLabels.push({ label: 'Manejo', value: 'Com Manejo' });
      if (!filters.incluir_finalizados) filtrosLabels.push({ label: 'Finalizados', value: 'Excluídos' });
      if (filtrosLabels.length === 0) filtrosLabels.push({ label: 'Filtros', value: 'Nenhum (todos os registros)' });

      await gerarRelatorioPDF({
        data,
        detalhado,
        empresa: empresaConfig,
        filtrosLabels,
      });
      toast.success('PDF gerado com sucesso');
    } catch (e) {
      console.error(e);
      toast.error('Falha ao gerar PDF: ' + (e.message || 'erro desconhecido'));
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setTimeout(gerar, 100);
  };

  if (loadingMod) return <div className="p-10 text-center text-muted-foreground">Carregando módulo...</div>;
  if (!financeiroAtivo) return <Navigate to="/" replace />;

  return (
    <div className="space-y-5" data-testid="financeiro-relatorio">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-cyan-600" /> Relatórios
          </h1>
          <p className="text-sm text-muted-foreground">
            Análise de serviços e despesas por múltiplos critérios
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30">
            <Switch
              checked={detalhado}
              onCheckedChange={setDetalhado}
              id="detalhado-toggle"
              data-testid="toggle-detalhado"
            />
            <Label htmlFor="detalhado-toggle" className="cursor-pointer text-sm">
              Detalhado (subserviços + despesas)
            </Label>
          </div>
          <Button
            onClick={handleExportPDF}
            disabled={!data || exporting}
            data-testid="btn-export-pdf"
          >
            <Printer className="w-4 h-4 mr-2" />
            {exporting ? 'Gerando PDF...' : 'Exportar PDF'}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'todos' ? '' : v }))}>
                <SelectTrigger data-testid="rel-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="pausado">Pausado</SelectItem>
                  <SelectItem value="aguardando_cliente">Aguardando Cliente</SelectItem>
                  <SelectItem value="aguardando_pagamento">Aguardando Pagamento</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Responsável</Label>
              <Select value={filters.responsavel_id || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, responsavel_id: v === 'todos' ? '' : v }))}>
                <SelectTrigger data-testid="rel-responsavel"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {responsaveis.map(r => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cliente</Label>
              <Select value={filters.cliente_id || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, cliente_id: v === 'todos' ? '' : v }))}>
                <SelectTrigger data-testid="rel-cliente"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="todos">Todos</SelectItem>
                  {clientes.map(c => <SelectItem key={c.id} value={c.id}>{c.nome_completo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo de Serviço</Label>
              <Select value={filters.tipo_servico_id || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, tipo_servico_id: v === 'todos' ? '' : v }))}>
                <SelectTrigger data-testid="rel-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo de Despesa</Label>
              <Select value={filters.tipo_despesa_id || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, tipo_despesa_id: v === 'todos' ? '' : v }))}>
                <SelectTrigger data-testid="rel-tipo-despesa"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="todos">Todos</SelectItem>
                  {tiposDespesa.filter(t => t.ativo !== false).map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mês</Label>
              <Input type="month" value={filters.mes} onChange={(e) => setFilters(f => ({ ...f, mes: e.target.value }))} data-testid="rel-mes" />
            </div>
            <div>
              <Label className="text-xs">De</Label>
              <Input type="date" value={filters.data_inicio} onChange={(e) => setFilters(f => ({ ...f, data_inicio: e.target.value, mes: '' }))} data-testid="rel-data-inicio" />
            </div>
            <div>
              <Label className="text-xs">Até</Label>
              <Input type="date" value={filters.data_fim} onChange={(e) => setFilters(f => ({ ...f, data_fim: e.target.value, mes: '' }))} data-testid="rel-data-fim" />
            </div>
            <div>
              <Label className="text-xs">Manejo</Label>
              <Select value={filters.com_manejo || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, com_manejo: v === 'todos' ? '' : v }))}>
                <SelectTrigger data-testid="rel-manejo"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="sim">Com Manejo</SelectItem>
                  <SelectItem value="nao">Sem Manejo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch
                  checked={filters.incluir_finalizados}
                  onCheckedChange={(v) => setFilters(f => ({ ...f, incluir_finalizados: v }))}
                  id="inc-final"
                />
                <Label htmlFor="inc-final" className="text-sm cursor-pointer">Incluir finalizados/cancelados</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={filters.somente_pagos}
                  onCheckedChange={(v) => setFilters(f => ({ ...f, somente_pagos: v }))}
                  id="somente-pagos"
                  data-testid="rel-somente-pagos"
                />
                <Label htmlFor="somente-pagos" className="text-sm cursor-pointer">Somente serviços pagos</Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="rel-limpar">
                <X className="w-3.5 h-3.5 mr-1.5" /> Limpar
              </Button>
              <Button onClick={gerar} disabled={loading} data-testid="rel-gerar">
                {loading ? 'Gerando...' : 'Gerar Relatório'}
              </Button>
            </div>
          </div>

          {/* v1.7 — Favoritos de filtros */}
          <div className="pt-3 border-t">
            <ReportFavorites
              modulo="financeiro"
              filtrosAtuais={filters}
              onAplicarFavorito={(f) => setFilters(prev => ({ ...prev, ...f }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Resultado exibido em tela (a exportação PDF é feita por gerarRelatorioPDF) */}
      {data && (
        <div className="space-y-4" data-testid="rel-resultado">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="rel-totais">
            <TotalCard
              icon={<FileText className="w-4 h-4" />}
              label="Serviços"
              value={String(data.totais.qtde_servicos)}
              hint={`Total: ${fmt(data.totais.valor_total_servicos)}`}
              color="cyan"
            />
            <TotalCard
              icon={<TrendingUp className="w-4 h-4" />}
              label="Recebido"
              value={fmt(data.totais.valor_recebido)}
              color="emerald"
            />
            <TotalCard
              icon={<DollarSign className="w-4 h-4" />}
              label="A Receber"
              value={fmt(data.totais.valor_pendente)}
              color="amber"
            />
            <TotalCard
              icon={<TrendingDown className="w-4 h-4" />}
              label="Despesas"
              value={fmt(data.totais.valor_despesas)}
              color="rose"
            />
            <TotalCard
              icon={<BarChart3 className="w-4 h-4" />}
              label="Lucro estimado"
              hint="(Recebido − Despesas)"
              value={fmt(data.totais.lucro_estimado)}
              color={data.totais.lucro_estimado >= 0 ? 'emerald' : 'rose'}
            />
          </div>

          {data.por_responsavel.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-3 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Resumo por Responsável
                </p>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground border-b">
                    <tr>
                      <th className="text-left py-2">Responsável</th>
                      <th className="text-right py-2">Qtde</th>
                      <th className="text-right py-2">Valor Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.por_responsavel.map(r => (
                      <tr key={r.id} className="border-b last:border-b-0">
                        <td className="py-2">{r.nome}</td>
                        <td className="py-2 text-right">{r.qtde}</td>
                        <td className="py-2 text-right font-semibold">{fmt(r.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              {data.servicos.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  Nenhum serviço para os filtros aplicados.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase">
                    <tr>
                      <th className="text-left p-2.5">Cliente</th>
                      <th className="text-left p-2.5">Serviço</th>
                      <th className="text-left p-2.5">Responsável</th>
                      <th className="text-left p-2.5">Status</th>
                      <th className="text-left p-2.5">Início</th>
                      <th className="text-right p-2.5">Valor</th>
                      <th className="text-right p-2.5">Recebido</th>
                      <th className="text-right p-2.5">Despesas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.servicos.map(s => (
                      <React.Fragment key={s.id}>
                        <tr className="border-t" data-testid={`rel-svc-${s.id}`}>
                          <td className="p-2.5 font-medium">{s.cliente_nome || '—'}</td>
                          <td className="p-2.5">
                            {s.titulo}
                            {s.tipo_servico_nome && <span className="block text-[11px] text-muted-foreground">{s.tipo_servico_nome}</span>}
                          </td>
                          <td className="p-2.5">{s.criado_por_nome || '—'}</td>
                          <td className="p-2.5">
                            <Badge variant="outline" className="text-[10px]">
                              {STATUS_LABEL[s.status] || s.status}
                            </Badge>
                          </td>
                          <td className="p-2.5">{fmtDate(s.data_inicio)}</td>
                          <td className="p-2.5 text-right font-semibold">{fmt(s.valor_total)}</td>
                          <td className="p-2.5 text-right text-emerald-600">{fmt(s.valor_pago)}</td>
                          <td className="p-2.5 text-right text-rose-600">{fmt(s.total_despesas)}</td>
                        </tr>
                        {detalhado && (
                          <>
                            {(s.itens || []).length > 0 && (
                              <tr className="bg-muted/20">
                                <td colSpan={8} className="p-2.5 pl-8 text-xs">
                                  <p className="font-semibold text-muted-foreground uppercase mb-1">Subserviços</p>
                                  <ul className="space-y-0.5">
                                    {s.itens.map((it, i) => (
                                      <li key={it.id || i} className="flex justify-between border-b border-dashed last:border-b-0 py-0.5">
                                        <span>▸ {it.descricao || <em>sem descrição</em>}</span>
                                        <span className="font-medium">{fmt(it.valor)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                            {(s.despesas_vinculadas || []).length > 0 && (
                              <tr className="bg-rose-50/40">
                                <td colSpan={8} className="p-2.5 pl-8 text-xs">
                                  <p className="font-semibold text-rose-600 uppercase mb-1">Despesas vinculadas</p>
                                  <ul className="space-y-0.5">
                                    {s.despesas_vinculadas.map(d => (
                                      <li key={d.id} className="flex justify-between border-b border-dashed last:border-b-0 py-0.5">
                                        <span>
                                          {fmtDate(d.data)} · {d.categoria_nome || 'sem categoria'}
                                          {d.observacoes ? ` · ${d.observacoes}` : ''}
                                        </span>
                                        <span className="font-medium text-rose-600">{fmt(d.valor)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/70 font-bold border-t-2">
                    <tr>
                      <td colSpan={5} className="p-2.5 text-right uppercase text-xs">Totais</td>
                      <td className="p-2.5 text-right">{fmt(data.totais.valor_total_servicos)}</td>
                      <td className="p-2.5 text-right text-emerald-700">{fmt(data.totais.valor_recebido)}</td>
                      <td className="p-2.5 text-right text-rose-700">{fmt(data.totais.valor_despesas)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </CardContent>
          </Card>

          {(data.despesas_detalhadas || []).length > 0 && (
            <Card data-testid="rel-despesas-detalhadas">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-3 flex items-center gap-1.5">
                  <TrendingDown className="w-3.5 h-3.5 text-rose-600" />
                  Despesas Detalhadas ({data.despesas_detalhadas.length})
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase">
                      <tr>
                        <th className="text-left p-2.5">Data</th>
                        <th className="text-left p-2.5">Tipo de Despesa</th>
                        <th className="text-left p-2.5">Serviço</th>
                        <th className="text-left p-2.5">Cliente</th>
                        <th className="text-left p-2.5">Observação</th>
                        <th className="text-right p-2.5">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.despesas_detalhadas.map(d => (
                        <tr key={d.id} className="border-t" data-testid={`rel-desp-${d.id}`}>
                          <td className="p-2.5">{fmtDate(d.data)}</td>
                          <td className="p-2.5">
                            <span
                              className="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
                              style={{ background: `${d.categoria_cor}22`, color: d.categoria_cor }}
                            >
                              {d.categoria_nome}
                            </span>
                          </td>
                          <td className="p-2.5">{d.servico_titulo || <span className="text-muted-foreground italic">—</span>}</td>
                          <td className="p-2.5">{d.cliente_nome || <span className="text-muted-foreground italic">—</span>}</td>
                          <td className="p-2.5 text-muted-foreground">{d.observacoes || d.descricao || <span className="italic">—</span>}</td>
                          <td className="p-2.5 text-right font-semibold text-rose-600">{fmt(d.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/70 font-bold border-t-2">
                      <tr>
                        <td colSpan={5} className="p-2.5 text-right uppercase text-xs">Total de Despesas</td>
                        <td className="p-2.5 text-right text-rose-700">{fmt(data.totais.valor_despesas)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-muted-foreground text-center">
            Gerado em {fmtDateTime(data.gerado_em)} por {data.gerado_por}
          </p>
        </div>
      )}
    </div>
  );
}

function TotalCard({ icon, label, value, hint, color = 'cyan' }) {
  const palette = {
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className={`p-3 rounded-lg border ${palette[color] || palette.cyan}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase opacity-80">
        {icon}{label}
      </div>
      <p className="text-xl font-bold mt-1">{value}</p>
      {hint && <p className="text-[11px] opacity-70 mt-0.5">{hint}</p>}
    </div>
  );
}
